import { io, Socket } from "socket.io-client";
import WebService from "./WebService";

export interface QrOrderEvent {
    id?: string;
    event?: string;
    data?: QrOrderPayload;
    created_at?: string;
}

export interface QrOrderPayload {
    orderId?: number | string;
    tableNo?: string | number;
    table_no?: string | number;
    loungeId?: string | number;
    lounge_id?: string | number;
    tenantId?: string | number;
    name?: string;
    mobile?: string;
    otp?: string;
    menus?: any[];
    timestamp?: string;
    type?: string;
    action?: string;
    menu_id?: string | number;
    tokenNumber?: string | number;
    token_number?: string | number;
    persons?: number;
    orderData?: any;
    [key: string]: any;
}

type QrOrderCallback = (order: QrOrderPayload) => void;

const getSocketUrl = () => {
    if (process.env.REACT_APP_POS_SOCKET_URL) {
        return process.env.REACT_APP_POS_SOCKET_URL;
    }
    const stored = localStorage.getItem("pos_socket_url");
    const host = window.location.hostname;
    if (stored && stored !== "http://localhost:3007") {
        return stored;
    }
    return (host === "localhost" || host === "127.0.0.1")
        ? "http://localhost:3007"
        : `http://${host}:3007`;
};

// ─── Unique key generator ─────────────────────────────────────────────────────
const getOrderKey = (order: QrOrderPayload, eventId?: string) => {
    const tableNo = order.tableNo || order.table_no || "";
    const loungeId = order.loungeId || order.lounge_id || "";
    if (order.type === 'POS_ORDER') {
        const action = order.action || 'GENERIC';
        const ts = order.timestamp || '';
        
        // Generate a stable representation of the menus to distinguish content updates
        const menusHash = Array.isArray(order.menus)
            ? order.menus.map((m: any) => `${m.id || m.menu_id || ''}:${m.quantity || ''}`).sort().join(',')
            : '';

        if (action === 'CLEAR_TABLE') {
            return `POS_CLEAR_${tableNo}_${ts}`;
        }
        return `POS_${action}_${tableNo}_${menusHash}_${ts}`;
    }

    if (order.orderId || tableNo) {
        return `${order.orderId || ""}_${tableNo}_${loungeId}`;
    }

    return `${eventId || ""}_${order.timestamp || ""}`;
};

class QrOrderRealtimeService {
    private socket: Socket | null = null;
    private pollInterval: NodeJS.Timeout | null = null;
    private seenEvents = new Set<string>()
    subscribe(callback: QrOrderCallback) {
        this.connectSocket(callback);
        this.startPolling(callback);

        return () => {
            this.socket?.off("qrCodeOrderPlaced");
            this.socket?.off("posOrderUpdate");
            this.socket?.off("kafkaEvent");
            this.socket?.disconnect();
            this.socket = null;
            if (this.pollInterval) {
                clearInterval(this.pollInterval);
                this.pollInterval = null;
            }
        };
    }

    private connectSocket(callback: QrOrderCallback) {
        if (this.socket) return;

        this.socket = io(getSocketUrl(), {
            transports: ["websocket", "polling"],
            reconnection: true,
            reconnectionDelay: 5000,
            timeout: 5000
        });

        this.socket.on("connect", () => {
            console.log("Socket Connected :", this.socket?.id);
        });

        this.socket.on("disconnect", () => {
            console.log("Socket Disconnected");
        });

        // ─── QR ORDER ────────────────────────────────────────────────────────────
        this.socket.on("qrCodeOrderPlaced", (data: QrOrderPayload) => {
            console.log("QR Order Placed :", data);
            this.emitOnce(data, callback);
        });

        // ─── POS ORDER UPDATE ─────────────────────────────────────────────────────
        this.socket.on("posOrderUpdate", (data: QrOrderPayload) => {
            console.log("=== posOrderUpdate received ===", {
                tableNo: data.tableNo || data.table_no,
                action: data.action,
                type: data.type,
                menusCount: data.menus?.length || 0,
            });

            const payload = { ...data, type: "POS_ORDER" as const };
            callback(payload);
        });

        // ─── ACTIVE TABLES UPDATE ────────────────────────────────────────────────
        this.socket.on("activeTablesUpdate", (data: any) => {
            console.log("activeTablesUpdate received:", data);
            callback({ ...data, type: "ACTIVE_TABLES" });
        });

        // ─── KAFKA EVENTS ─────────────────────────────────────────────────────────
        this.socket.on("kafkaEvent", (message: any) => {
            console.log("Kafka Event received:", message?.event);
            if (message?.event === "qrCodeOrderPlaced" && message?.data) {
                this.emitOnce(message.data, callback);
            }
            if (message?.event === "posOrderUpdate" && message?.data) {
                callback({ ...message.data, type: "POS_ORDER" });
            }
            if (message?.event === "activeTablesUpdate" && message?.data) {
                callback({ ...message.data, type: "ACTIVE_TABLES" });
            }
        });

        // ─── ORDER UPDATED (POS internal updates) ────────────────────────────────
        // POS ke andar koi bhi change ho to yeh event bhi aa sakta hai
        this.socket.on("orderUpdated", (data: any) => {
            console.log("orderUpdated received:", data);
            // Agar table_no hai to waiter ko bhi notify karo
            if (data?.table_no || data?.tableNo) {
                callback({
                    ...data,
                    type: "POS_ORDER",
                    tableNo: data.tableNo || data.table_no,
                    table_no: data.table_no || data.tableNo,
                });
            }
        });
    }

    private startPolling(callback: QrOrderCallback) {
        const poll = () => {
            const tenantId = localStorage.getItem("tenantId") || "default";
            let clientId = localStorage.getItem("waiter_client_id");
            if (!clientId) {
                clientId = "wcid_" + Math.random().toString(36).substring(2, 15) + "_" + Date.now();
                localStorage.setItem("waiter_client_id", clientId);
            }
            WebService.getAPI<any>({
                action: "/waiter/events/pull",
                body: { tenantId, clientId },
                isShowError: false,
            })
                .then((response: any) => {
                    const events: QrOrderEvent[] = response?.events || [];
                    events.forEach((event) => {
                        if (event.event === "activeTablesUpdate" && event.data) {
                            callback({ ...event.data, type: "ACTIVE_TABLES" });
                        } else if (
                            (event.event === "qrCodeOrderPlaced" || event.event === "posOrderUpdate") && event.data) {
                            const payload = event.event === "posOrderUpdate" ? { ...event.data, type: "POS_ORDER" } : event.data;
                            if (event.event === "posOrderUpdate") {
                                callback(payload);
                            } else {
                                this.emitOnce(payload, callback, event.id);
                            }
                        }
                    });
                })
                .catch(() => {
                    // Silent fail — polling errors ignore karo
                });
        };
        poll();
        this.pollInterval = setInterval(poll, 2000);
    }

    private emitOnce(
        order: QrOrderPayload,
        callback: QrOrderCallback,
        eventId?: string
    ) {
        const key = getOrderKey(order, eventId);
        if (this.seenEvents.has(key)) {
            console.log("Duplicate event ignored:", key);
            return;
        }

        this.seenEvents.add(key);
        // Seen events set zyada bada na ho jaye — 500 se zyada hone par purane clear karo
        if (this.seenEvents.size > 500) {
            const firstKey = this.seenEvents.values().next().value;
            if (firstKey) this.seenEvents.delete(firstKey);
        }
        console.log("New order event emitted to UI:", key);
        callback(order);
    }

    // ─── Manually clear seen events (e.g. on logout/table clear) ───────────────
    clearSeenEvents() {
        this.seenEvents.clear();
    }

    // ─── Table clear hone par specific table ke events clear karo ──────────────
    clearTableEvents(tableNo: string) {
        const keysToDelete: string[] = [];
        this.seenEvents.forEach(key => {
            if (key.includes(`_${tableNo}_`)) {
                keysToDelete.push(key);
            }
        });
        keysToDelete.forEach(key => this.seenEvents.delete(key));
        console.log(`Cleared ${keysToDelete.length} seen events for table ${tableNo}`);
    }
}

const qrOrderRealtimeService = new QrOrderRealtimeService();

export default qrOrderRealtimeService;